import React from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  if (totalPages <= 1) return null

  const getPageNumbers = () => {
    const pages: (number | string)[] = []
    const margin = 2 // How many pages to show around current

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      // Always include page 1
      pages.push(1)

      if (currentPage > margin + 2) {
        pages.push('...')
      }

      const start = Math.max(2, currentPage - margin)
      const end = Math.min(totalPages - 1, currentPage + margin)

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      if (currentPage < totalPages - (margin + 1)) {
        pages.push('...')
      }

      // Always include last page
      pages.push(totalPages)
    }
    return pages
  }

  const baseBtnClass =
    'h-10 border border-slate-200 flex items-center justify-center transition-all bg-white hover:bg-slate-50 text-slate-600 text-xs font-bold'
  const activeBtnClass = '!bg-slate-900 !border-slate-900 !text-white'
  const disabledBtnClass = 'opacity-40 cursor-not-allowed hover:bg-white'

  const pageNumbers = getPageNumbers()

  return (
    <div className='flex items-center gap-0.5 sm:gap-1 shadow-sm rounded overflow-hidden flex-wrap justify-center'>
      <button
        type='button'
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1}
        className={`${baseBtnClass} px-2 sm:px-4 rounded-l border-r-0 ${
          currentPage <= 1 ? disabledBtnClass : ''
        }`}
      >
        <ChevronLeft className='h-4 w-4 sm:mr-1' />
        <span className='hidden sm:inline'>PREVIOUS</span>
      </button>

      {pageNumbers.map((p, idx) => {
        const prev = pageNumbers[idx - 1]
        const next = pageNumbers[idx + 1]
        const stableKey =
          typeof p === 'number' ? p : `ellipsis-${String(prev)}-${String(next)}`
        return (
          <button
            key={stableKey}
            type='button'
            disabled={p === '...'}
            onClick={() => typeof p === 'number' && onPageChange(p)}
            className={`
            ${baseBtnClass} min-w-[36px] sm:min-w-[40px] px-1.5 sm:px-2 border-r-0 last:border-r text-xs
            ${p === currentPage ? activeBtnClass : ''}
            ${p === '...' ? 'cursor-default hover:bg-white' : ''}
          `}
          >
            {p}
          </button>
        )
      })}

      <button
        type='button'
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages}
        className={`${baseBtnClass} px-2 sm:px-4 rounded-r last:border-r ${
          currentPage >= totalPages ? disabledBtnClass : ''
        }`}
      >
        <span className='hidden sm:inline'>NEXT</span>
        <ChevronRight className='h-4 w-4 sm:ml-1' />
      </button>
    </div>
  )
}

export default Pagination
